import { fromDisplayKmh } from '../constants.ts';
import { clamp } from '../math.ts';
import { reachIsDisk, type ReachAxes } from './reach.ts';

/**
 * Share of accelerate held back as speed climbs. Zero at rest, then a large
 * fraction of the reach, released only in the last part of the climb so a
 * nose click can still poke through.
 */
const HUMP_HOLD = 0.9;
/** Level where the hold starts to fall away. Below this the hold is full. */
const HUMP_RELEASE = 0.82;
const PLANE_TURN_SCALE = 0.75;
const PLANE_BRAKE_SURPLUS = 0.8;
const TOP_SPEED_MULTIPLE = 2;
/** Full lateral click on the plane lands just under the threshold. */
const TURN_OFF_PLANE = 0.92;

/** Coast length and reaches after planing. Callers pass these onward; they do not recompute the curve. */
export type PlaningProjection = {
  /** 0 at rest, 1 at and above planing speed. */
  level: number;
  coastPx: number;
  axes: ReachAxes;
};

/**
 * Speed-dependent coast and reaches. Below planing, drag pulls the coast back
 * and the authored reaches stay. At and above, drag is gone, the turn narrows,
 * brake grows with the surplus, and accelerate closes at twice planing speed.
 */
export function planingFeel(
  speedPx: number,
  planingSpeedKmh: number,
  authored: ReachAxes,
): PlaningProjection {
  const planingPx = Math.max(fromDisplayKmh(planingSpeedKmh), 1e-6);
  const speed = Math.max(0, speedPx);
  const level = clamp(speed / planingPx, 0, 1);

  if (level < 1) {
    const release = level < HUMP_RELEASE ? 1 : (1 - level) / (1 - HUMP_RELEASE);
    const drag = authored.accel * HUMP_HOLD * level * release;

    return {
      level,
      coastPx: Math.max(0, speed - drag),
      axes: { turn: authored.turn, accel: authored.accel, brake: authored.brake },
    };
  }

  const excess = clamp((speed - planingPx) / planingPx, 0, 1);
  const coastPx = speed;
  const room = Math.max(0, planingPx * TOP_SPEED_MULTIPLE - coastPx);

  return {
    level: 1,
    coastPx,
    axes: {
      turn: authored.turn * PLANE_TURN_SCALE,
      accel: Math.min(authored.accel, room),
      brake: authored.brake * (1 + PLANE_BRAKE_SURPLUS * excess),
    },
  };
}

/**
 * Planing off is the identity: full coast, authored reaches, empty meter, chord kept.
 * Deleting this module means callers pass `boat.speed`, the authored reaches, and no speed override.
 */
export function applyPlaning(
  enabled: boolean,
  speedPx: number,
  planingSpeedKmh: number,
  authored: ReachAxes,
): PlaningProjection {
  if (!enabled) {
    return {
      level: 0,
      coastPx: Math.max(0, speedPx),
      axes: { turn: authored.turn, accel: authored.accel, brake: authored.brake },
    };
  }

  return planingFeel(speedPx, planingSpeedKmh, authored);
}

/** Null while the reaches still match a disk, so the circle painters stay on that path. */
export function planingReach(axes: ReachAxes): ReachAxes | null {
  return reachIsDisk(axes) ? null : axes;
}

/**
 * Kept speed for a turn that starts on the plane. `null` means keep the chord:
 * below the threshold, dead ahead, or a click that is already at or under planing speed.
 * Hardness is how far `offset` sits toward the widest turn this oval allows, squared.
 */
export function planingTurnSpeed(input: {
  /** Chord speed, already including any session turn tax. */
  chord: number;
  /** Signed angle between the entry heading and the chord. */
  offset: number;
  speedPx: number;
  planingSpeedKmh: number;
  /** Lateral reach of the oval the click was chosen in. */
  turnPx: number;
  coastPx: number;
}): number | null {
  const planingPx = Math.max(fromDisplayKmh(input.planingSpeedKmh), 1e-6);

  if (input.speedPx < planingPx || input.chord <= planingPx) {
    return null;
  }

  if (Math.abs(input.offset) < 1e-9) {
    return null;
  }

  const maxOffset = Math.atan2(Math.max(input.turnPx, 1), Math.max(input.coastPx, 1));

  if (maxOffset < 1e-6) {
    return null;
  }

  const hardness = clamp(Math.abs(input.offset) / maxOffset, 0, 1) ** 2;

  if (hardness === 0) {
    return null;
  }

  const floor = planingPx * TURN_OFF_PLANE;

  return input.chord + (floor - input.chord) * hardness;
}
